import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ArrowRight, Coins, Sparkles } from "lucide-react";
import { BRAND } from "@/lib/api";
import bannerRu from "@/assets/postad/hero-banner-ru.jpg";
import bannerEn from "@/assets/postad/hero-banner-en.jpg";
import forwhomRealtors from "@/assets/postad/forwhom-realtors.jpeg";
import forwhomOwners from "@/assets/postad/forwhom-owners.jpeg";

const BANNER = {
  ru: bannerRu,
  en: bannerEn,
};

const TAB_IMG = [forwhomRealtors, forwhomOwners];

const T = {
  ru: {
    badge: "Соответствует 152-ФЗ «О персональных данных»",
    h1a: "Планируйте встречи",
    h1b: "без бесконечных писем",
    subA: "Доверьте планирование Тагдада — гости ",
    subU: "сами записываются и переносят встречи",
    subB: " — в календарях, почте и мессенджерах. Без переписки.",
    start: "Начать бесплатно",
    login: "У меня есть аккаунт",
    supported: "Поддерживаются:",
    more: "и многие другие",
    forwhom: "Для кого Тагдада",
    tabs: [
      { l: "Риелторы", t: "Риелторы и агенты", d: "Одна ссылка на объект: показ, отбор арендаторов и покупателей, самостоятельная запись на просмотр и уведомления о каждой заявке." },
      { l: "Собственники", t: "Собственники и арендодатели", d: "Задайте удобное время для показов, подключите календарь и заранее узнайте, кто придёт на просмотр — без звонков и переписки." },
    ],
    freeA: "Все функции доступны —",
    freeB: "Бесплатно.",
    freeC: "Всегда.",
    credits: "бесплатных кредитов и больше",
    getCredits: "Получить 400 кредитов",
    bannerAlt: "Тагдада Недвижимость — 2-комнатная квартира, 65 м², записаться на просмотр",
  },
  en: {
    badge: "Compliant with Russian Federal Law 152-FZ on personal data",
    h1a: "Schedule meetings",
    h1b: "without endless emails",
    subA: "Let Tagdada handle the scheduling — guests ",
    subU: "book and reschedule meetings themselves",
    subB: " — across calendars, email and messengers. No back-and-forth.",
    start: "Start for free",
    login: "I have an account",
    supported: "Supported:",
    more: "and many more",
    forwhom: "Who Tagdada is for",
    tabs: [
      { l: "Realtors", t: "Realtors & agents", d: "One link per property: viewings, tenant and buyer screening, self-service booking and a notification for every request." },
      { l: "Landlords", t: "Landlords & owners", d: "Set convenient viewing times, connect your calendar and know upfront who's coming to the viewing — no calls, no back-and-forth." },
    ],
    freeA: "Every feature included —",
    freeB: "Free.",
    freeC: "Forever.",
    credits: "free credits and more",
    getCredits: "Get 400 credits",
    bannerAlt: "Tagdada Real Estate — 2-room apartment, 65 m², book a viewing",
  },
};

const INTEGRATIONS = ["Yandex", "Telemost", "Telegram", "Google"];
const ROTATE_MS = 5000;

function ForWhomCard({ t }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setTimeout(() => setActive((a) => (a + 1) % t.tabs.length), ROTATE_MS);
    return () => clearTimeout(id);
  }, [active, paused, t.tabs.length]);

  const tab = t.tabs[active];
  return (
    <div
      data-testid="hero-forwhom-card"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="rounded-[28px] border border-orange-100/80 bg-white/70 backdrop-blur-sm p-5 sm:p-8 shadow-[0_20px_60px_-30px_rgba(242,107,42,0.35)]"
    >
      <p className="text-xs sm:text-sm font-extrabold uppercase tracking-[0.2em] text-[#F26B2A]">{t.forwhom}</p>
      <div className="mt-6 min-h-[260px] sm:min-h-[220px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="grid sm:grid-cols-[1.05fr_1fr] gap-5 sm:gap-7 items-center"
            data-testid={`hero-forwhom-panel-${active}`}
          >
            <div className="rounded-2xl bg-[#FFF6EF] overflow-hidden aspect-[3/2]">
              <img src={TAB_IMG[active]} alt={tab.t} className="h-full w-full object-cover mix-blend-multiply" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">{tab.t}</h3>
              <p className="mt-3 text-base text-slate-500 leading-relaxed">{tab.d}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4" role="tablist">
        {t.tabs.map((tb, i) => (
          <button
            key={tb.l}
            role="tab"
            aria-selected={i === active}
            data-testid={`hero-forwhom-tab-${i}`}
            onClick={() => setActive(i)}
            className="group text-left"
          >
            <span className="block h-1 rounded-full bg-orange-100 overflow-hidden">
              {i === active ? (
                <motion.span
                  key={`${active}-${paused}`}
                  className="block h-full bg-[#F26B2A] rounded-full"
                  initial={{ width: paused ? "100%" : "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: paused ? 0 : ROTATE_MS / 1000, ease: "linear" }}
                />
              ) : null}
            </span>
            <span className={`mt-2 block truncate text-sm font-semibold transition-colors ${i === active ? "text-[#F26B2A]" : "text-slate-500 group-hover:text-slate-800"}`}>
              {tb.l}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PostAdHero({ lang }) {
  const t = T[lang] || T.ru;
  const fade = (d) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: d } });

  return (
    <section
      data-testid="postad-hero"
      className="relative overflow-hidden bg-[radial-gradient(ellipse_70%_60%_at_0%_0%,#FFE9DC_0%,transparent_60%),radial-gradient(ellipse_60%_50%_at_100%_100%,#FFF0E6_0%,transparent_60%),#FFFBF8]"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:pt-10 sm:pb-24 grid gap-6 lg:gap-x-10 lg:gap-y-6 lg:grid-cols-[1.3fr_1fr] lg:grid-rows-[auto_auto_1fr]">
        {/* Intro */}
        <div className="lg:col-start-1 lg:row-start-1 min-w-0">
          <motion.span {...fade(0)} data-testid="hero-152fz-badge"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200/80 px-4 py-2 text-xs sm:text-sm font-medium text-slate-600 shadow-sm">
            <ShieldCheck className="h-4 w-4 text-[#F26B2A] shrink-0" /> {t.badge}
          </motion.span>
          <motion.h1 {...fade(0.05)} data-testid="hero-title"
            className="mt-6 text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[4.6rem] font-extrabold tracking-[-0.03em] text-[#1A1210]">
            {t.h1a}{" "}
            <span className="text-[#F26B2A]">{t.h1b}</span>
          </motion.h1>
          <motion.p {...fade(0.1)} className="mt-7 text-lg sm:text-xl text-slate-500 leading-relaxed max-w-2xl">
            {t.subA}
            <span className="text-slate-800 underline decoration-[#F26B2A]/40 decoration-[3px] underline-offset-[6px]">{t.subU}</span>
            {t.subB}
          </motion.p>
          <motion.div {...fade(0.15)} className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href={BRAND.register} target="_blank" rel="noopener noreferrer" data-testid="hero-start-free-btn"
              className="inline-flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#F26B2A] to-[#F5843C] hover:from-[#E05A19] hover:to-[#EE7630] text-white text-base sm:text-lg font-semibold px-8 py-4 shadow-[0_12px_30px_-12px_rgba(242,107,42,0.8)] transition-all active:scale-[0.98]">
              {t.start} <ArrowRight className="h-5 w-5" />
            </a>
            <a href={BRAND.login} target="_blank" rel="noopener noreferrer" data-testid="hero-login-btn"
              className="inline-flex items-center justify-center rounded-2xl bg-slate-100/80 hover:bg-slate-200/70 text-slate-800 text-base sm:text-lg font-medium px-8 py-4 transition-colors">
              {t.login}
            </a>
          </motion.div>
          <motion.div {...fade(0.2)} data-testid="hero-supported"
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-base">
            <span className="text-slate-400">{t.supported}</span>
            {INTEGRATIONS.map((n) => <span key={n} className="font-medium text-slate-800">{n}</span>)}
            <span className="italic text-slate-400">{t.more}</span>
          </motion.div>
        </div>

        {/* Property banner */}
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55, delay: 0.1 }}
          className="lg:col-start-2 lg:row-start-1 lg:row-end-3 self-start w-full max-w-[520px] mx-auto lg:max-w-none">
          <div data-testid="hero-property-banner"
            className="rounded-[28px] bg-white p-2.5 sm:p-3 border border-orange-100/70 shadow-[0_30px_70px_-35px_rgba(242,107,42,0.45)]">
            <img src={BANNER[lang] || BANNER.ru} alt={t.bannerAlt} className="w-full h-auto rounded-[22px]" data-testid="hero-property-banner-img" />
          </div>
        </motion.div>

        {/* Free credits */}
        <motion.div {...fade(0.25)} data-testid="hero-credits-card"
          className="lg:col-start-2 lg:row-start-3 self-start w-full max-w-[520px] mx-auto lg:max-w-none rounded-[28px] bg-white/80 border border-orange-100/80 p-6 sm:p-7 shadow-[0_20px_60px_-30px_rgba(242,107,42,0.35)]">
          <h3 className="text-2xl sm:text-[1.8rem] font-extrabold tracking-tight leading-tight text-[#1A1210]">
            {t.freeA}<br />
            <span className="text-[#F26B2A]">{t.freeB}</span> {t.freeC}
          </h3>
          <div className="mt-5 flex items-center gap-4">
            <span className="h-14 w-14 rounded-2xl bg-[#FFF1E8] flex items-center justify-center shrink-0">
              <Coins className="h-7 w-7 text-[#F26B2A]" />
            </span>
            <span className="text-5xl font-extrabold tracking-tight text-[#F26B2A] leading-none">400</span>
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500 leading-snug max-w-[9rem]">{t.credits}</span>
          </div>
          <a href={BRAND.register} target="_blank" rel="noopener noreferrer" data-testid="hero-get-credits-btn"
            className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-2xl bg-gradient-to-r from-[#F26B2A] to-[#F5843C] hover:from-[#E05A19] hover:to-[#EE7630] text-white text-base sm:text-lg font-bold px-6 py-4 shadow-[0_12px_30px_-12px_rgba(242,107,42,0.8)] transition-all active:scale-[0.98]">
            <Sparkles className="h-5 w-5" /> {t.getCredits} <ArrowRight className="h-5 w-5" />
          </a>
        </motion.div>

        {/* For whom */}
        <motion.div {...fade(0.3)} className="lg:col-start-1 lg:row-start-2 lg:row-end-4 self-start min-w-0">
          <ForWhomCard t={t} />
        </motion.div>
      </div>
    </section>
  );
}
